import { Link, Navigate } from "react-router-dom";
import { Check, Phone, ArrowRight, MapPin } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines, Eyebrow } from "../components/Reveal";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { QuoteForm } from "../components/QuoteForm";
import { FaqSection } from "../components/FaqSection";
import { FinalCta } from "../components/FinalCta";
import { SUBURB_PAGES, WORK_ITEMS, FAQS, BUSINESS } from "../content";
import { motion } from "framer-motion";
import { track } from "../lib/track";

const SUBURB_FAQ_QS = [
  "Do you offer regular house cleaning?",
  "Can I book weekly or fortnightly cleans?",
  "Will I usually have the same cleaner?",
  "How do I get a quote?",
];

export const SuburbPage = ({ slug }) => {
  const suburb = SUBURB_PAGES.find((s) => s.slug === slug);
  if (!suburb) return <Navigate to="/service-areas" replace />;

  const slider = WORK_ITEMS.find((w) => w.id === suburb.slider);
  const others = SUBURB_PAGES.filter((s) => s.slug !== suburb.slug).slice(0, 5);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "House Cleaning",
    provider: {
      "@type": "LocalBusiness",
      name: "Pure Bliss Clean",
      telephone: "+61468434936",
      url: "https://www.pureblissclean.net/",
    },
    areaServed: { "@type": "Place", name: `${suburb.suburb}, Victoria` },
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.pureblissclean.net/" },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: "https://www.pureblissclean.net/service-areas" },
      { "@type": "ListItem", position: 3, name: `House Cleaning ${suburb.suburb}` },
    ],
  };

  return (
    <>
      <Seo
        title={`House Cleaning ${suburb.suburb} | Regular & End of Lease Cleans | Pure Bliss Clean`}
        description={`Reliable house cleaning in ${suburb.suburb} — weekly & fortnightly cleans, deep cleaning and end of lease cleaning from local cleaners Pure Bliss Clean. Get a free quote.`}
        path={`/${suburb.slug}`}
        schema={[schema, breadcrumb]}
      />

      <section data-testid="suburb-hero" className="relative overflow-hidden bg-brand-mist pt-32 pb-16 lg:pt-44 lg:pb-24">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full border-[40px] border-teal-100/60" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <Reveal>
                <Eyebrow>House Cleaning · {suburb.suburb}</Eyebrow>
              </Reveal>
              <MaskedLines
                onLoad
                lines={["Reliable House", `Cleaning in`, suburb.suburb]}
                className="font-display text-4xl font-black leading-[1.05] text-brand-ink sm:text-5xl lg:text-6xl"
              />
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg"
              >
                {suburb.intro}
              </motion.p>
              <Reveal delay={0.3}>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <a
                    href="#quote"
                    data-testid="suburb-quote-button"
                    onClick={() => track("cta_click", { label: `suburb_quote_${slug}` })}
                    className="group inline-flex items-center gap-2.5 rounded-full bg-brand-teal px-8 py-4 font-display text-base font-bold text-white shadow-xl shadow-teal-900/20 transition-[background-color,transform] duration-200 hover:-translate-y-1 hover:bg-teal-600"
                  >
                    Get a Free Quote
                    <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </a>
                  <a
                    href={BUSINESS.phoneHref}
                    data-testid="suburb-call-button"
                    onClick={() => track("click_to_call", { location: `suburb_${slug}` })}
                    className="inline-flex items-center gap-2.5 rounded-full border border-slate-300 bg-white px-8 py-4 font-display text-base font-bold text-brand-ink transition-colors duration-200 hover:border-brand-teal hover:text-brand-teal"
                  >
                    <Phone className="h-5 w-5 text-brand-teal" /> {BUSINESS.phone}
                  </a>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.25}>
              <div className="overflow-hidden rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-900/5">
                <img src={suburb.image} alt={suburb.imageAlt} className={`${suburb.ratio || "aspect-[4/3]"} w-full object-cover`} loading="eager" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Your Local Cleaners</Eyebrow>
            <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">
              A Cleaner {suburb.suburb} Can Rely On
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">{suburb.local}</p>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">
              And if something has genuinely been missed, let us know promptly and we'll work with you to make it
              right. That's the kind of long-term, local service we're proud of.
            </p>
            <ul className="mt-8 space-y-3.5">
              {suburb.popular.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm font-semibold text-brand-ink">
                  <span className="mt-0.5 rounded-full bg-teal-50 p-1 text-brand-teal"><Check className="h-3.5 w-3.5" /></span>
                  {item} in {suburb.suburb}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-sm text-slate-500">
              Also servicing nearby suburbs —{" "}
              <Link to="/service-areas" className="font-semibold text-brand-teal underline-offset-4 hover:underline">
                see all areas we clean
              </Link>
              .
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <BeforeAfterSlider {...slider} />
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-mist">
        <div className="mx-auto max-w-3xl px-6 py-24 lg:py-32">
          <Reveal className="mb-10 text-center">
            <div className="flex justify-center"><Eyebrow>Free Quote</Eyebrow></div>
            <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">
              Get Your Free {suburb.suburb} Cleaning Quote
            </h2>
            <p className="mx-auto mt-4 max-w-md text-slate-600">
              Tell us about your {suburb.suburb} home and what you need cleaned — we'll be in touch to discuss your
              quote.
            </p>
          </Reveal>
          <div id="quote">
            <QuoteForm defaultService="Regular House Cleaning" defaultSuburb={suburb.suburb} source={`suburb_${slug}`} />
          </div>
        </div>
      </section>

      <FaqSection
        items={FAQS.filter((f) => SUBURB_FAQ_QS.includes(f.q))}
        heading={`House Cleaning ${suburb.suburb} — FAQs`}
      />

      <section className="mx-auto max-w-7xl px-6 pb-24">
        <Reveal>
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <p className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest text-slate-500">
              <MapPin className="h-4 w-4 text-brand-teal" /> We also clean in
            </p>
            <ul className="mt-4 flex flex-wrap gap-3">
              {others.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}`}
                    className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-brand-ink transition-colors hover:border-teal-300 hover:text-brand-teal"
                  >
                    {s.suburb}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/service-areas" className="rounded-full bg-brand-mist px-4 py-2 text-sm font-bold text-brand-teal">
                  All areas
                </Link>
              </li>
            </ul>
          </div>
        </Reveal>
      </section>

      <FinalCta />
    </>
  );
};
